import { useEffect, useState } from "react";
import { useNavigate, useParams, Link, Navigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import usePageTitle from "@/hooks/use-page-title";
import OrderItemsEditor, { emptyItem, calcItemTotals, calcExtra } from "@/components/OrderItemsEditor";
import CatalogPicker from "@/components/CatalogPicker";
import ExtrasEditor from "@/components/ExtrasEditor";

export default function EditOrder() {
  const { id } = useParams();
  usePageTitle(id ? `Edit ${id}` : "Edit Order");
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [customers, setCustomers] = useState([]);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get(`/orders/${id}`).then((r) => {
      const o = r.data.order;
      setForm({
        customer_id: o.customer_id || "",
        order_date: (o.order_date || (o.created_at || "").slice(0, 10)) || "",
        delivery_date: o.delivery_date || "",
        shipping_address: o.shipping_address || "",
        transport_charge: o.transport_charge || 0,
        transport_gst_rate: o.transport_gst_rate || 18,
        transport_gst_mode: o.transport_gst_mode || "none",
        installation_charge: o.installation_charge || 0,
        installation_gst_rate: o.installation_gst_rate || 18,
        installation_gst_mode: o.installation_gst_mode || "none",
        lamination: !!o.lamination,
        eyelets: !!o.eyelets,
        installation_required: !!o.installation_required,
        delivery_required: o.delivery_required !== false,
        priority: o.priority || "normal",
        remarks: o.remarks || "",
      });
      // Reconstruct items array; if legacy single-product, wrap into one item
      if (Array.isArray(o.items) && o.items.length > 0) {
        setItems(o.items.map((it) => ({
          product_category: it.product_category || "Flex Printing",
          product_name: it.product_name || "",
          material: it.material || "",
          printing_type: it.printing_type || "",
          quantity: it.quantity || 1,
          gst_rate: it.gst_rate || 0,
          gst_mode: it.gst_mode || "excluded",
          sizes: it.sizes && it.sizes.length ? it.sizes : [{ width: 0, height: 0, unit: "ft", quantity: 1, rate: 0 }],
          addons: it.addons || [],
        })));
      } else {
        // Legacy — build one item from top-level fields
        const legacy = {
          product_category: o.product_category || "Flex Printing",
          product_name: o.product_name || "",
          material: o.material || "",
          printing_type: o.printing_type || "",
          quantity: o.quantity || 1,
          gst_rate: 18,
          gst_mode: "none",
          sizes: [{
            width: o.width || 0, height: o.height || 0, unit: o.size_unit || "ft",
            quantity: o.quantity || 1, rate: (o.width && o.height && o.estimated_cost) ? Math.round((o.estimated_cost / (o.width * o.height * (o.quantity || 1))) * 100) / 100 : 0,
          }],
          addons: [],
        };
        setItems([legacy]);
      }
    }).catch((e) => setErr(formatApiError(e)));
  }, [id]);

  if (!isAdmin) return <Navigate to={`/orders/${id}`} replace />;
  if (!form) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const set = (k, v) => setForm({ ...form, [k]: v });
  const itemsSub = items.reduce((s, it) => s + calcItemTotals(it).subtotal, 0);
  const itemsTax = items.reduce((s, it) => s + calcItemTotals(it).tax, 0);
  const tExtra = calcExtra(form.transport_charge, form.transport_gst_rate, form.transport_gst_mode);
  const iExtra = calcExtra(form.installation_charge, form.installation_gst_rate, form.installation_gst_mode);
  const grand = itemsSub + itemsTax + tExtra.total + iExtra.total;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (items.length === 0 || !items.some((i) => calcItemTotals(i).total > 0)) {
      setErr("Please add at least one product with a rate");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        items: items.map((it) => ({
          ...it,
          quantity: Number(it.quantity) || 1,
          gst_rate: Number(it.gst_rate) || 0,
          gst_mode: it.gst_mode || "excluded",
          sizes: (it.sizes || []).map((s) => ({
            width: Number(s.width) || 0, height: Number(s.height) || 0,
            unit: s.unit || "ft", quantity: Number(s.quantity) || 1, rate: Number(s.rate) || 0,
          })),
          addons: (it.addons || []).map((a) => ({
            name: a.name, quantity: Number(a.quantity) || 1, rate: Number(a.rate) || 0,
          })),
        })),
        transport_charge: Number(form.transport_charge) || 0,
        transport_gst_rate: Number(form.transport_gst_rate) || 0,
        installation_charge: Number(form.installation_charge) || 0,
        installation_gst_rate: Number(form.installation_gst_rate) || 0,
        estimated_cost: grand,
      };
      await api.put(`/orders/${id}`, payload);
      nav(`/orders/${id}`);
    } catch (e2) { setErr(formatApiError(e2)); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-5xl space-y-6" data-testid="edit-order-root">
      <Link to={`/orders/${id}`} className="text-sm text-muted-foreground hover:text-[#FF6B00] inline-flex items-center gap-2">
        <ArrowLeft size={14} /> Back to order
      </Link>
      <div className="border-b border-[hsl(var(--border))] pb-6">
        <div className="overline mb-2">Edit</div>
        <h1 className="font-display text-4xl font-black tracking-tighter">Edit Order {id}</h1>
        <p className="text-sm text-muted-foreground mt-1">Admin can modify items, GST treatment, extras and customer. Advance payments are not affected.</p>
      </div>

      <form onSubmit={submit} className="space-y-6">
        <div className="card-swiss p-5 space-y-3">
          <div className="overline">Customer</div>
          <select required value={form.customer_id} onChange={(e) => set("customer_id", e.target.value)}
            className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none focus:border-[#FF6B00]" data-testid="edit-order-customer-select">
            {customers.map((c) => (
              <option key={c.id} value={c.id}>{c.name} {c.company ? `— ${c.company}` : ""} · {c.phone}</option>
            ))}
          </select>
        </div>

        <div className="card-swiss p-5 space-y-4">
          <div className="overline">Products</div>
          <CatalogPicker
            onPick={(item) => { setItems((p) => [...p, item]); toast.success(`${item.product_name.split(" · ")[0]} added`); }}
            onCustom={() => { setItems((p) => [...p, emptyItem()]); toast.success("Custom product added"); }}
          />
          <OrderItemsEditor items={items} setItems={setItems} testIdPrefix="edit-item" />
        </div>

        <div className="card-swiss p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="overline block mb-2">Order Date</label>
            <input type="date" value={form.order_date} onChange={(e) => set("order_date", e.target.value)}
              className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none" />
          </div>
          <div>
            <label className="overline block mb-2">Delivery Date</label>
            <input type="date" value={form.delivery_date} onChange={(e) => set("delivery_date", e.target.value)}
              className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none" />
          </div>
          <div className="md:col-span-2">
            <div className="overline mb-2">Extras & Their GST</div>
            <ExtrasEditor form={form} set={(patch) => setForm({ ...form, ...patch })} />
          </div>
          <div>
            <label className="overline block mb-2">Priority</label>
            <select value={form.priority} onChange={(e) => set("priority", e.target.value)}
              className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none">
              <option value="low">Low</option><option value="normal">Normal</option>
              <option value="high">High</option><option value="urgent">Urgent</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="overline block mb-2">Shipping Address</label>
            <textarea rows={2} value={form.shipping_address} onChange={(e) => set("shipping_address", e.target.value)}
              className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none" />
          </div>
          <div className="md:col-span-2 flex flex-wrap gap-4">
            {[["lamination", "Lamination"], ["eyelets", "Eyelets"], ["installation_required", "Installation"], ["delivery_required", "Delivery"]].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form[k]} onChange={(e) => set(k, e.target.checked)} />
                {l}
              </label>
            ))}
          </div>
          <div className="md:col-span-2">
            <label className="overline block mb-2">Remarks</label>
            <textarea rows={2} value={form.remarks} onChange={(e) => set("remarks", e.target.value)}
              className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none" />
          </div>
        </div>

        <div className="card-swiss p-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div><div className="overline">Subtotal</div><div className="font-mono">₹{Math.round(itemsSub).toLocaleString("en-IN")}</div></div>
          <div><div className="overline">GST</div><div className="font-mono">₹{Math.round(itemsTax + tExtra.tax + iExtra.tax).toLocaleString("en-IN")}</div></div>
          <div><div className="overline">Extras</div><div className="font-mono">₹{Math.round(tExtra.base + iExtra.base).toLocaleString("en-IN")}</div></div>
          <div><div className="overline text-[#FF6B00]">Grand Total</div><div className="font-mono font-black text-lg text-[#FF6B00]" data-testid="edit-order-grand-total">₹{Math.round(grand).toLocaleString("en-IN")}</div></div>
        </div>

        {err && <div className="text-sm text-red-600" data-testid="edit-order-form-error">{err}</div>}

        <div className="flex justify-end gap-3 pt-2">
          <Link to={`/orders/${id}`} className="btn-swiss btn-outline">Cancel</Link>
          <button type="submit" disabled={saving} className="btn-swiss btn-accent" data-testid="save-edit-order-btn">
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
